import { useEffect, useState } from "react";
import { notifications as notifApi } from "../lib/endpoints";

/**
 * Notification counts, shared by every component that asks for them.
 *
 *  - `count`  items still needing a decision (pending join requests on your rides)
 *  - `unread` items you haven't opened the feed on yet
 *  - `total`  everything currently in the feed
 *
 * `count` and `unread` are independent: a request can be read but still un-acted, or settled by
 * the other party before you ever looked.
 *
 * The counts live in one module-level store rather than in each caller's state. Two reasons:
 * a second mounted consumer would otherwise open its own 30s poll against the same endpoint, and
 * — the visible one — opening the feed marks everything read, which has to clear the sidebar badge
 * *now*. With per-component state the badge kept its dot until the next poll, up to 30s after the
 * user was already looking at the list. Callers that change the counts server-side call `refresh()`.
 */

const EMPTY = { count: 0, unread: 0, total: 0 };
const POLL_MS = 30_000;

let counts = EMPTY;
let timer = null;
const listeners = new Set();

const publish = (next) => {
  counts = next;
  listeners.forEach((l) => l(next));
};

/** Pull the counts and notify every subscriber. Failures keep the previous values. */
export const refreshCounts = () =>
  notifApi
    .count()
    .then((c) => publish({ count: c.actionable || 0, unread: c.unread || 0, total: c.total || 0 }))
    .catch(() => {/* keep previous — a dropped poll shouldn't blank the badge */});

const startPolling = () => {
  if (timer || typeof document === "undefined" || document.hidden) return;
  timer = setInterval(refreshCounts, POLL_MS);
};

const stopPolling = () => {
  if (timer) { clearInterval(timer); timer = null; }
};

/**
 * Drop the counts and stop polling — call on logout.
 *
 * These live at module scope, so unlike component state they survive a client-side navigation.
 * Without this the next person to sign in on the same tab would briefly see the previous user's
 * badge, and the poll would keep running against a session that no longer exists.
 */
export const resetCounts = () => {
  stopPolling();
  publish(EMPTY);
};

const onVisibility = () => {
  if (document.hidden) stopPolling();
  else { refreshCounts(); startPolling(); }
};

export const useIncomingCount = () => {
  const [snapshot, setSnapshot] = useState(counts);

  useEffect(() => {
    listeners.add(setSnapshot);
    // First subscriber starts the shared poll; the last one out turns it off.
    if (listeners.size === 1) {
      refreshCounts();
      startPolling();
      document.addEventListener("visibilitychange", onVisibility);
    } else {
      setSnapshot(counts);
    }
    return () => {
      listeners.delete(setSnapshot);
      if (listeners.size === 0) {
        stopPolling();
        document.removeEventListener("visibilitychange", onVisibility);
      }
    };
  }, []);

  return { ...snapshot, refetch: refreshCounts };
};
